import React, { useRef, useEffect } from 'react'
import heroVideo from '../../assets/qRaptor Video - 1 Booth (1).mp4'

const FADE_SEC = 2.5 // crossfade window in seconds

const videoStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center center',
  pointerEvents: 'none',
  transition: `opacity ${FADE_SEC}s ease-in-out`,
}

/**
 * HeroBg — Two video elements that crossfade at the loop point.
 * Both are always playing during the overlap so there is never
 * a blank or stuttered frame — video A fades out while video B
 * (already playing from its beginning) fades in beneath it.
 */
export default function HeroBg() {
  const aRef = useRef(null)
  const bRef = useRef(null)
  const crossfadingRef = useRef(false)
  // track which video is the "active" (foreground) one
  const foregroundRef = useRef('a')

  useEffect(() => {
    const a = aRef.current
    const b = bRef.current
    if (!a || !b) return

    // Initial state
    a.style.opacity = '1'
    a.style.zIndex = '1'
    b.style.opacity = '0'
    b.style.zIndex = '0'

    const triggerCrossfade = (outgoing, incoming) => {
      crossfadingRef.current = true

      // Pre-roll the incoming video so it's already buffered at frame 0
      incoming.currentTime = 0
      incoming.play().catch(() => {})

      // Stack incoming on top, then kick off the CSS transition
      incoming.style.zIndex = '2'
      outgoing.style.zIndex = '1'

      // Force a reflow so the browser registers the initial opacity
      // before the transition begins
      void incoming.getBoundingClientRect()

      incoming.style.opacity = '1'
      outgoing.style.opacity = '0'

      setTimeout(() => {
        // Swap logical roles: incoming is now foreground
        foregroundRef.current = foregroundRef.current === 'a' ? 'b' : 'a'
        outgoing.pause()
        outgoing.currentTime = 0
        crossfadingRef.current = false
      }, FADE_SEC * 1000)
    }

    const handleTimeUpdate = (e) => {
      if (crossfadingRef.current) return
      const video = e.target
      // Only the foreground video drives the crossfade
      const isForeground =
        (foregroundRef.current === 'a' && video === a) ||
        (foregroundRef.current === 'b' && video === b)
      if (!isForeground) return

      const { currentTime, duration } = video
      if (!duration) return

      if (duration - currentTime <= FADE_SEC) {
        const other = video === a ? b : a
        triggerCrossfade(video, other)
      }
    }

    a.addEventListener('timeupdate', handleTimeUpdate)
    b.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      a.removeEventListener('timeupdate', handleTimeUpdate)
      b.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [])

  return (
    <>
      <video ref={aRef} autoPlay muted playsInline aria-hidden="true"
        style={{ ...videoStyle, zIndex: 1, opacity: 1 }}>
        <source src={heroVideo} type="video/mp4" />
      </video>
      <video ref={bRef} muted playsInline aria-hidden="true"
        style={{ ...videoStyle, zIndex: 0, opacity: 0 }}>
        <source src={heroVideo} type="video/mp4" />
      </video>

      {/* Dark gradient overlay — always on top of both videos */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 3,
          pointerEvents: 'none',
          background:
            'linear-gradient(to bottom, rgba(7,7,26,0.82) 0%, rgba(7,7,26,0.38) 50%, rgba(7,7,26,0.60) 100%)',
        }}
      />
    </>
  )
}